import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useIdentity } from '../../identity/state/useIdentity';
import type { CompanyResponse } from '../../identity/types';
import { companiesGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const PAGE_SIZE = 10;

/** Masks everything but the last 4 characters — the same convention Module 2's own SsnLast4/
 * IdentificationLast4 fields already use for sensitive identifiers, applied here to EIN in list view. */
function maskEin(ein: string | null): string {
  if (!ein) {
    return '—';
  }
  const visible = ein.slice(-4);
  return `${'•'.repeat(Math.max(ein.length - 4, 0))}${visible}`;
}

export function CompanyListPage() {
  const { me, refresh } = useIdentity();
  const [companies, setCompanies] = useState<CompanyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<CompanyResponse | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [name, setName] = useState('');
  const [ein, setEin] = useState('');
  const [isActive, setIsActive] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCompanies(await companiesGateway.listMine());
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load companies.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  // Client-side search/pagination: EPN.Identity.Api's company list has no server-side search or paging
  // support today (see docs/staging-data.md) — this filters/pages the real API response rather than
  // inventing a server capability that doesn't exist.
  const filtered = useMemo(
    () => companies.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())),
    [companies, search],
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function startEdit(company: CompanyResponse) {
    setEditing(company);
    setName(company.name);
    setEin(company.ein ?? '');
    setIsActive(company.isActive);
    setShowAddForm(false);
  }

  async function handleSaveEdit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    try {
      await companiesGateway.update(editing.id, { name, ein: ein || null, isActive });
      setEditing(null);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save company.'));
    }
  }

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!me) return;
    setError(null);
    try {
      await companiesGateway.create({ name, ein: ein || null, initialAdminUserId: me.userId });
      setName('');
      setEin('');
      setShowAddForm(false);
      // The real backend (CompanyService.CreateAsync()) atomically grants the current user a CompanyAdmin
      // membership on the company just created, alongside the company itself — refreshing identity here
      // (not just the company list) is what makes BusinessViewSwitcher pick up that new membership
      // immediately, with no logout/relogin or page reload required.
      await Promise.all([load(), refresh()]);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not create company.'));
    }
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Companies</h2>
        <button
          type="button"
          onClick={() => {
            setShowAddForm((v) => !v);
            setEditing(null);
            setName('');
            setEin('');
          }}
        >
          {showAddForm ? 'Cancel' : 'Add company'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {showAddForm && (
        <form className="inline-form" onSubmit={handleAdd}>
          <input placeholder="Company name" value={name} onChange={(e) => setName(e.target.value)} required />
          <input placeholder="EIN (optional)" value={ein} onChange={(e) => setEin(e.target.value)} />
          <button type="submit">Create</button>
        </form>
      )}

      {editing && (
        <form className="inline-form" onSubmit={handleSaveEdit}>
          <strong>Editing {editing.name}</strong>
          <input placeholder="Company name" value={name} onChange={(e) => setName(e.target.value)} required />
          <input placeholder="EIN" value={ein} onChange={(e) => setEin(e.target.value)} />
          <label>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
          </label>
          <button type="submit">Save</button>
          <button type="button" className="link" onClick={() => setEditing(null)}>
            Cancel
          </button>
        </form>
      )}

      <input
        className="search-box"
        placeholder="Search companies…"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />

      {loading && <p className="muted">Loading…</p>}
      {!loading && pageItems.length === 0 && <p className="muted">No companies found.</p>}

      {!loading && pageItems.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>EIN</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{maskEin(c.ein)}</td>
                <td>{c.isActive ? 'Active' : 'Inactive'}</td>
                <td>
                  <button type="button" className="link" onClick={() => startEdit(c)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span className="muted">
            Page {page} of {totalPages}
          </span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </section>
  );
}
